import { useModalContext } from 'utils/contexts/modal';
import { useToastNotification } from 'utils/hooks/useToastNotification';
import { Table } from 'ui/table';
import { PaginationFooter } from 'ui/paginationFooter';
import { useClientPaginatedState } from 'ui/table/hooks/useClientPaginatedState';
import { TableHeader } from 'ui/tableHeader';
import { TableRows } from 'ui/tableRows';
import { InputText } from 'ui/inputs/inputText';
import { FlexSpacer } from 'ui/flexSpacer';

import { FacebookIntegrationsHeader } from './FacebookIntegrationsHeader';
import { FacebookIntegrationModals } from './modals';
import { useFacebookIntegrationContext } from '../contexts/FacebookIntegrationContext';
import { useFbcaTranslations } from '../hooks/useTranslations';
import { FacebookModalState } from '../domain/modal';
import { useFacebookLoginContext } from '../contexts/FacebookLoginContext';
import { useSubmitAdAccounts } from '../hooks/useSubmitAdAccounts';
import { configureTableColumns, facebookIntegrationsFilterFn } from '../utils';

export const FacebookIntegrationsPage = () => {
  const { showModal } = useModalContext<FacebookModalState>();
  const {
    facebookIntegrations,
    hasIntegrationsError,
    isGettingIntegrations,
    isNotUnsubscribed,
    isUnsubscribed,
    isUnsubscribing,
    selectedId,
    targetId,
  } = useFacebookIntegrationContext();
  const { isSuccess, isLoading } = useSubmitAdAccounts();

  const { loginCode } = useFacebookLoginContext();
  const { t } = useFbcaTranslations();

  const {
    currentPage,
    filter,
    itemsPerPage,
    numItems,
    rows,
    setCurrentPage,
    setFilter,
    setItemsPerPage,
    setSortDirection,
    setSortedBy,
    sortDirection,
    sortedBy,
  } = useClientPaginatedState({
    rows: facebookIntegrations,
    sortedBy: 'id',
    filterFn: facebookIntegrationsFilterFn,
  });

  const emptyStateText = loginCode ? t('emptyState.noResults') : t('emptyState.reloginText');

  useToastNotification({
    positive: isUnsubscribed,
    negative: isNotUnsubscribed,
    positiveMessage: t('toasts.deleteSuccess', { selectedId }),
    negativeMessage: t('toasts.deleteError'),
    enabled: !isUnsubscribing || !!selectedId,
  });

  useToastNotification({
    positive: isSuccess,
    positiveMessage: t('toasts.submitSuccess'),
    enabled: !isLoading && !isGettingIntegrations,
  });

  const COLUMNS = configureTableColumns(targetId, showModal);

  if (isGettingIntegrations) return null;

  return (
    <>
      <FacebookIntegrationsHeader />
      <FlexSpacer justifyContent="flex-end">
        <InputText
          data-test="filter-enabled-accounts"
          value={filter}
          onChange={setFilter}
          placeholder={t('table.filterPlaceholder')}
        />
      </FlexSpacer>
      <Table>
        <TableHeader
          columns={COLUMNS}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          sortedBy={sortedBy}
          setSortedBy={setSortedBy}
        />
        <TableRows columns={COLUMNS} rows={rows} />
      </Table>
      {rows.length === 0 && (
        <FlexSpacer alignItems="center" justifyContent="center" padding="cardPadding">
          {hasIntegrationsError ? t('emptyState.fetchError') : emptyStateText}
        </FlexSpacer>
      )}
      <PaginationFooter
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        numItems={numItems}
        onUpdate={({ page, pageSize }) => {
          setCurrentPage(page);
          setItemsPerPage(pageSize);
        }}
      />

      <FacebookIntegrationModals />
    </>
  );
};
